import { defineStore } from "pinia";
import { ref } from "vue";
import { INSPECTION_LINKS_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { LOGGER } from "@/util/logger";
import { clone } from "@/util/clone";
import { MODULE_TYPES } from "@/constants/InspectionConstants";
import { useInspectionStore } from "@/stores/inspection";
import { useConfigStore } from "@/stores/config";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { STRUCTURE_UNIT_TYPE } from "@/constants/Inspections";

export const useBearingsStore = defineStore("bearings", () => {
  let pageData = ref({});
  const inspectionStore = useInspectionStore();
  const configStore = useConfigStore();

  const loadDataObject = async (moduleType, brkey, inspectionId, structure) => {
    if (moduleType === MODULE_TYPES.INSPECTION) {
      //if moduleType is inspection, load data from pouchDB
      convertToInspModuleObject(structure);
    } else if (moduleType === MODULE_TYPES.MANAGEMENT && brkey && inspectionId) {
      //if moduleType is management, load data from service call
      await getInspLinkDataObject(brkey, inspectionId);
    }
  };

  const getBearings = () => {
    return clone(pageData.value);
  };

  const saveData = async (moduleType, value, structure) => {
    let isSuccess = false;
    if (moduleType === MODULE_TYPES.INSPECTION) {
      convertToSaveInspModuleObject(value, structure);
      isSuccess = true;
    } else if (moduleType === MODULE_TYPES.MANAGEMENT) {
      isSuccess = await updateData(convertToSaveInspLinkObject(value));
    }
    return isSuccess;
  };

  const updateData = async (payload) => {
    let isSuccess = true;
    const url = INSPECTION_LINKS_ENDPOINT.UPDATE_BEARINGS;
    await axios
      .put(url, payload)
      .then((response) => {
        LOGGER.logEvent(LOGGER.EventAction.Update, LOGGER.EventCat.RequestResponse, "Update Bearings", response);
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
        isSuccess = false;
      });
    return isSuccess;
  };

  const convertToSaveInspModuleObject = (bearings, structure) => {
    structure.structureBearings = bearings.structureBearings;
    structure.userInspection.overallBearingCondRating = bearings.overallBearingCondRating;
  };

  const getInspLinkDataObject = async (brkey, inspectionId) => {
    pageData.value = {};
    let url = INSPECTION_LINKS_ENDPOINT.GET_BEARINGS.replace("{brkey}", brkey).replace("{inspectionId}", inspectionId);

    await axios
      .get(url)
      .then((response) => {
        convertToInspLinkObject(response.data.data);
      })
      .catch((e) => {
        LOGGER.logException(e);
      });
  };

  const convertToInspModuleObject = (structure) => {
    pageData.value = {
      brkey: structure.structureBridge.brkey,
      inspectionId: structure.inspection.inspectionId,
      overallBearingCondRating: structure.userInspection.overallBearingCondRating,
      structureBearings: structure.structureBearings,
      bearingLocations: convertStructureUnitsToBearingLocations(
        inspectionStore.getFilteredStructureUnits([STRUCTURE_UNIT_TYPE.APRAS_SPAN], false),
      ),
    };
  };

  const convertToSaveInspLinkObject = (bearings) => {
    return {
      brkey: bearings.brkey,
      inspectionId: bearings.inspectionId,
      overallBearingCondRating: bearings.overallBearingCondRating,
      structureBearings: bearings.structureBearings,
      inspectionComments: bearings.inspectionComments,
    };
  };

  const convertToInspLinkObject = (response) => {
    pageData.value = {
      brkey: response.brkey,
      inspectionId: response.inspectionId,
      overallBearingCondRating: response.overallBearingCondRating,
      structureBearings: response.structureBearings,
      bearingLocations: convertStructureUnitsToBearingLocations(response.structureUnits),
      inspectionComments: response.inspectionComments,
    };
  };

  const convertStructureUnitsToBearingLocations = (structureUnits) => {
    return structureUnits
      ?.filter((b) => ["B", "C", "P", "W"].includes(b.legacyStructureType))
      ?.map((b) => ({
        title: `${configStore.getReferenceValue(REFERENCE_TABLE.STRUCTURE_UNIT_TYPE, b.legacyStructureType, true)}-${
          b.strUnitLabel
        }`,
        value: b.strUnitKey,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  };

  return {
    getBearings,
    saveData,
    loadDataObject,
    configStore,
    REFERENCE_TABLE,
    inspectionStore,
  };
});
